import { ContentfulBlogPostFields, ContentfulEntry, ContentfulProjectFields } from '@devtinker/shared';
import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { getEntries } from '../lib/contentful';

interface CategoryCount {
  name: string;
  count: number;
}

const Projects: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [projects, setProjects] = useState<ContentfulEntry<ContentfulProjectFields>[]>([]);
  const [allProjects, setAllProjects] = useState<ContentfulEntry<ContentfulProjectFields>[]>([]);
  const [categories, setCategories] = useState<CategoryCount[]>([]);
  const [recentPosts, setRecentPosts] = useState<ContentfulEntry<ContentfulBlogPostFields>[]>([]);
  const [loading, setLoading] = useState(true);
  const selectedCategory = searchParams.get('category');

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const entries = await getEntries('project');
        const sortedEntries = entries as ContentfulEntry<ContentfulProjectFields>[];
        setAllProjects(sortedEntries);
        setProjects(sortedEntries);
        
        // Extract categories from project technologies
        const categoryMap = new Map<string, number>();
        sortedEntries.forEach(project => {
          const technologies = project.fields.technologies || [];
          technologies.forEach((tech: string) => {
            categoryMap.set(tech, (categoryMap.get(tech) || 0) + 1);
          });
        });
        
        const extractedCategories = Array.from(categoryMap.entries())
          .map(([name, count]) => ({ name, count }))
          .sort((a, b) => b.count - a.count);
        setCategories(extractedCategories);
        
        // Fetch recent blog posts
        const blogEntries = await getEntries('blogPost');
        const sortedBlogs = (blogEntries as ContentfulEntry<ContentfulBlogPostFields>[])
          .sort((a, b) => new Date(b.fields.publishDate).getTime() - new Date(a.fields.publishDate).getTime())
          .slice(0, 5);
        setRecentPosts(sortedBlogs);
      } catch (error) {
        console.error('Error fetching projects:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, []);

  // Filter projects when category changes
  useEffect(() => {
    if (selectedCategory) {
      const filtered = allProjects.filter(project => 
        project.fields.technologies?.includes(selectedCategory)
      );
      setProjects(filtered);
    } else {
      setProjects(allProjects);
    }
  }, [selectedCategory, allProjects]);

  // Mock data for when Contentful is not configured
  const mockProjects = [
    {
      sys: { id: '1', type: 'Entry', createdAt: '2021-06-01', updatedAt: '2021-06-01', contentType: { sys: { id: 'project' } } },
      fields: {
        title: 'Artistry and Coffee Web Experience',
        slug: 'artistry-and-coffee',
        description: 'Writing about creative pursuits while enjoying the next cup of coffee. Art, Photography, Writing, and anything else that gets those creative impulses flowing.',
        projectUrl: 'https://www.artistryandcoffee.com/',
        technologies: ['WordPress', 'Photography', 'Content'],
        status: 'active' as const,
        featuredImage: undefined
      }
    },
    {
      sys: { id: '2', type: 'Entry', createdAt: '2021-06-01', updatedAt: '2021-06-01', contentType: { sys: { id: 'project' } } },
      fields: {
        title: 'Gathered Goods Market',
        slug: 'gathered-goods-market',
        description: 'Explore the meticulously curated collection where they belief that beauty comes from both form and function, and the best items should be used throughout our daily lives and decorative enough to be enjoyed.',
        projectUrl: 'https://www.gatheredgoodsmarket.com/',
        technologies: ['E-commerce', 'Shopify'],
        status: 'active' as const,
        featuredImage: undefined
      }
    },
    {
      sys: { id: '3', type: 'Entry', createdAt: '2021-06-01', updatedAt: '2021-06-01', contentType: { sys: { id: 'project' } } },
      fields: {
        title: 'Web Manager',
        slug: 'web-manager',
        description: 'Application to manage common websites that you routinely use during a given activity, whether it is social media, blogging and/or research, or any other routine activity. Create a profile and open all of those sites with the press of a single button.',
        projectUrl: '/projects/web-manager',
        technologies: ['React', 'TypeScript', 'Chrome Extension'],
        status: 'active' as const,
        featuredImage: undefined
      }
    },
    {
      sys: { id: '4', type: 'Entry', createdAt: '2021-06-01', updatedAt: '2021-06-01', contentType: { sys: { id: 'project' } } },
      fields: {
        title: 'DevTinker Experiments',
        slug: 'devtinker-experiments',
        description: 'Smaller explorations and testing of coding concepts, where else to put them on display but in the lab. Check out our Experiments section for more interesting activities.',
        projectUrl: '/experiments',
        technologies: ['JavaScript', 'CSS', 'HTML5'],
        status: 'active' as const,
        featuredImage: undefined
      }
    }
  ];

  const displayProjects = projects.length > 0 ? projects : mockProjects;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading projects...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-12 text-center">
        <h1 className="text-5xl font-bold text-gray-900 mb-4">Projects</h1>
        <p className="text-xl text-gray-600">Our Portfolio of Digital Creations</p>
        {selectedCategory && (
          <div className="mt-4 flex items-center justify-center gap-2">
            <span className="text-sm text-gray-600">Filtered by:</span>
            <span className="inline-flex items-center gap-2 bg-green-100 text-green-800 px-4 py-2 rounded-full font-medium">
              {selectedCategory}
              <button
                onClick={() => setSearchParams({})}
                className="hover:text-green-900"
                aria-label="Clear filter"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </span>
          </div>
        )}
      </div>

      {/* Projects Grid */}
      <div className="space-y-12">
        {displayProjects.map((project, index) => (
          <div
            key={project.sys.id}
            className={`flex flex-col ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'} gap-8 items-center bg-white rounded-lg shadow-sm hover:shadow-lg transition-shadow border border-gray-200 overflow-hidden`}
          >
            {/* Project Image */}
            <div className="w-full md:w-1/2">
              {project.fields.featuredImage ? (
                <img
                  src={project.fields.featuredImage.fields.file.url}
                  alt={project.fields.title}
                  className="w-full h-80 object-cover"
                />
              ) : (
                <div className="w-full h-80 bg-gradient-to-br from-green-500 to-green-700 flex items-center justify-center">
                  <div className="text-white text-center p-8">
                    <svg className="w-24 h-24 mx-auto mb-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                    </svg>
                    <h3 className="text-2xl font-bold">{project.fields.title}</h3>
                  </div>
                </div>
              )}
            </div>

            {/* Project Content */}
            <div className="w-full md:w-1/2 p-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                {project.fields.title}
              </h2>
              
              <p className="text-gray-600 leading-relaxed mb-6">
                {project.fields.description}
              </p>

              {/* Technologies */}
              {project.fields.technologies && project.fields.technologies.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-6">
                  {project.fields.technologies.map((tech, techIndex) => (
                    <span
                      key={techIndex}
                      className="text-xs font-semibold uppercase tracking-wide text-green-600 bg-green-50 px-3 py-1 rounded-full"
                    >
                      {tech}
                    </span>
                  ))}
                </div>
              )}

              {/* View More Button - External URL or Internal Project Page */}
              {project.fields.projectUrl ? (
                // External URL - open in new tab
                project.fields.projectUrl.startsWith('http') ? (
                  <a
                    href={project.fields.projectUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-6 py-3 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 transition-colors"
                  >
                    Visit Project
                    <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </a>
                ) : (
                  <Link
                    to={project.fields.projectUrl}
                    className="inline-flex items-center px-6 py-3 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 transition-colors"
                  >
                    View More
                    <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </Link>
                )
              ) : (
                // No external URL - link to internal project detail page
                <Link
                  to={`/projects/${project.fields.slug}`}
                  className="inline-flex items-center px-6 py-3 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 transition-colors"
                >
                  View Details
                  <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </Link>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Sidebar-like section */}
      <aside className="mt-16 bg-gray-50 p-8 rounded-lg">
        <div className="grid md:grid-cols-2 gap-8">
          {/* Recent Posts */}
          <div>
            <h3 className="text-xl font-bold text-gray-900 mb-4">Recent Posts</h3>
            <ul className="space-y-3">
              {recentPosts.length > 0 ? (
                recentPosts.map((post) => (
                  <li key={post.sys.id}>
                    <Link 
                      to={`/blog/${post.fields.slug}`}
                      className="text-gray-700 hover:text-green-600 transition-colors text-sm"
                    >
                      {post.fields.title}
                    </Link>
                  </li>
                ))
              ) : (
                <li>
                  <Link to="/blog" className="text-gray-700 hover:text-green-600 transition-colors text-sm">
                    Explore our latest articles and guides
                  </Link>
                </li>
              )}
            </ul>
          </div>

          {/* Tags */}
          <div>
            <h3 className="text-xl font-bold text-gray-900 mb-4">Tags</h3>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setSearchParams({})}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-full font-medium text-sm transition-all ${
                  !selectedCategory 
                    ? 'bg-green-600 text-white shadow-md' 
                    : 'bg-white text-gray-700 border border-gray-200 hover:border-green-600 hover:text-green-600'
                }`}
              >
                <span>All</span>
                <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                  !selectedCategory
                    ? 'bg-white/20 text-white'
                    : 'bg-gray-100 text-gray-600'
                }`}>
                  {allProjects.length}
                </span>
              </button>
              {categories.map((category) => (
                <button
                  key={category.name}
                  onClick={() => setSearchParams({ category: category.name })}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-full font-medium text-sm transition-all ${
                    selectedCategory === category.name
                      ? 'bg-green-600 text-white shadow-md'
                      : 'bg-white text-gray-700 border border-gray-200 hover:border-green-600 hover:text-green-600'
                  }`}
                >
                  <span>{category.name}</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                    selectedCategory === category.name
                      ? 'bg-white/20 text-white'
                      : 'bg-gray-100 text-gray-600'
                  }`}>
                    {category.count}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </aside>
    </div>
  );
};

export default Projects;
